// src/pages/DriverDashboard.tsx

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ref as dbRef, set, remove, onValue, off } from "firebase/database";
import { collection, query, where, limit, getDocs } from "firebase/firestore";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, rtdb } from "../../../firebase";
import type { AppDispatch, RootState } from "../../../redux/store";
import {
  listenToDriverData,
  listenToDriverTripRequests,
  listenToDriverParcelRequests,
  setActiveVehicle,
  selectCurrentDriver,
  selectPendingTripRequest,
  selectPendingParcelRequest,
  selectListings,
} from "../../../redux/fleetSlice";
import {
  Car, Layers, Package, Home, Bell,
  ChevronRight, ChevronLeft, AlertCircle, Star, RefreshCw,
  Navigation, User, ArrowRight, Loader2, TrendingUp,
  ChevronDown, Plus, Heart, MapPin, Edit3, Trash2, Image as ImageIcon,
  GitMerge, Tag, X,
} from "lucide-react";

// ── Imported pages rendered inside dashboard ──────────────────────────────
import DriverCars from "./DriverCars";
import RideBookings from "./RideBookings";
import ParcelBookings from "./ParcelBookings";

import appLogo from "../../../assets/app/logo.png";

// ── Category helpers ──
import {
  CAR_BRANDS, carBrandName,
  CLASS_TYPES, CLASS_TYPE, classTypeName,
  CAR_SIZES, CAR_SIZE, carSizeName,
  BIKE_TYPES, CAR_TYPES, JET_TYPES, RIDE_TYPES,
  fuelTypeName, transmissionName,
} from "../../../CATEGORIES/OperatorCategories";

const DRIVER_ONLINE  = "https://europe-west1-eartlas.cloudfunctions.net/driverOnline";
const DRIVER_OFFLINE = "https://europe-west1-eartlas.cloudfunctions.net/driverOffline";
const DRIVER_PING    = "https://europe-west1-eartlas.cloudfunctions.net/driverPing";

/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — same palette as AtlasHome
   (cream, warm gold, crimson red, near-black text)
═══════════════════════════════════════════════════════════ */
const H = {
  bg:        "#FBF6EE",
  panel:     "#FFFFFF",
  panelSoft: "#FCEFD4",

  dark:      "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red:       "#C8202E",
  redDark:   "#9E1521",
  redSoft:   "#FBE7E9",

  gold:      "#F2A93E",
  goldDark:  "#D4900F",
  goldSoft:  "#FCEFD4",

  green:     "#1DB954",
  greenSoft: "#E5F8ED",
  amber:     "#D4900F",
  amberSoft: "#FCEFD4",
  blue:      "#3D7BD9",
  blueSoft:  "#EAF1FC",

  border:    "#EEDFC4",
  divider:   "#F3E6CE",
};

const RATE_LABELS: Record<string, string> = {
  perKm: "/km", perPerson: "/person", perDay: "/day", perCar: "flat", perSeat: "/seat", perSurge: "surge",
};

function getGreeting() {
  const h = new Date().getHours();
  if (h < 5) return "Up late";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Good night";
}

const ALL_VEHICLE_TYPES = [...(CAR_TYPES as any[]), ...(BIKE_TYPES as any[]), ...(JET_TYPES as any[])];

function vehicleName(id: any): string {
  return (ALL_VEHICLE_TYPES.find((v) => v.id === id) as any)?.name ?? "Vehicle";
}

function vehicleTypeItem(id: any): any {
  return ALL_VEHICLE_TYPES.find((v: any) => v.id === id);
}

function rateUnit(type: string): string {
  return RATE_LABELS[type] ?? type;
}

function extrasToArr(obj: Record<string, number> = {}): { name: string; price: number }[] {
  return Object.entries(obj).map(([name, price]) => ({ name, price }));
}

function discKeyFromObj(obj: Record<string, boolean> = {}): string {
  if (obj.is80) return "80%";
  if (obj.is50) return "50%";
  if (obj.is25) return "25%";
  if (obj.is10) return "10%";
  if (obj.is5) return "5%";
  return "None";
}

/* ──────────────────────────────────────────────────────────
   SPEC ITEM — labelled facts
────────────────────────────────────────────────────────────── */
function SpecItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="hr-spec-item">
      <span className="hr-spec-lbl">{label}</span>
      <span className="hr-spec-val">{value}</span>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   ROW THUMB — vehicle thumbnail with fallback
────────────────────────────────────────────────────────────── */
function RowThumb({ listing }: { listing: any }) {
  const typeItem = vehicleTypeItem(listing.vehicleType);
  const rideItem = RIDE_TYPES.find((r: any) => r.id === listing.rideType);
  const photo    = listing.photos?.[0];
  const typeImg  = typeItem?.image;
  const rideImg  = rideItem?.image;

  const [photoFailed, setPhotoFailed]     = useState(false);
  const [typeImgFailed, setTypeImgFailed] = useState(false);
  const [rideImgFailed, setRideImgFailed] = useState(false);

  if (photo && !photoFailed) {
    return <img src={photo} alt="" onError={() => setPhotoFailed(true)} />;
  }
  if (typeImg && !typeImgFailed) {
    return <img src={typeImg} alt="" onError={() => setTypeImgFailed(true)} />;
  }
  if (rideImg && !rideImgFailed) {
    return <img src={rideImg} alt="" onError={() => setRideImgFailed(true)} />;
  }
  return (
    <div className="hr-row-thumb-ph">
      {typeItem?.icon ?? <Car size={30} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS — enhanced with car card styling from DriverCars
═══════════════════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600;700&family=Montserrat:wght@400;500;600;700;800;900&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
:root{
  --serif:'Cormorant Garamond',Georgia,serif;
  --sans:'Montserrat',-apple-system,sans-serif;
}
html,body{height:100%;background:${H.bg};}
.hr-root{width:100vw;min-height:100dvh;background:${H.bg};font-family:var(--sans);color:${H.dark};display:flex;flex-direction:column;}
.hr-root button{font-family:inherit;}
.hr-root ::-webkit-scrollbar{width:8px;height:8px;}
.hr-root ::-webkit-scrollbar-thumb{background:${H.border};border-radius:8px;}

.hr-reveal{opacity:0;transform:translateY(12px);transition:opacity .5s ease var(--d,0ms),transform .5s cubic-bezier(.22,1,.36,1) var(--d,0ms);}
.hr-on .hr-reveal{opacity:1;transform:none;}
.hr-body{flex:1;display:flex;overflow:hidden;position:relative;}

@media(min-width:960px){
  .hr-sidebar{width:250px;flex-shrink:0;background:${H.panel};border-right:1px solid ${H.border};display:flex;flex-direction:column;overflow-y:auto;transition:width .18s ease;overflow-x:hidden;}
  .hr-main{flex:1;overflow-y:auto;min-width:0;}
  .hr-mobile-header{display:none!important;}
  .hr-tabbar{display:none!important;}
}
@media(max-width:959px){
  .hr-body{flex-direction:column;}
  .hr-sidebar{display:none!important;}
  .hr-main{flex:1;overflow-y:auto;padding-bottom:76px;}
  .hr-mobile-header{flex-shrink:0;position:sticky;top:0;z-index:30;background:rgba(255,255,255,.92);backdrop-filter:blur(10px);border-bottom:1px solid ${H.border};display:flex;align-items:center;height:58px;padding:0 16px;gap:10px;}
}

/* ── sidebar ── */
.hr-sidebar-logo{padding:22px 20px 16px;border-bottom:1px solid ${H.border};display:flex;align-items:center;justify-content:space-between;gap:10px;}
.hr-sidebar-logo img{width:34px;height:34px;border-radius:10px;object-fit:cover;border:1.5px solid ${H.gold};flex-shrink:0;}
.hr-sidebar-brand{font-family:var(--serif);font-size:17px;font-weight:700;color:${H.dark};line-height:1.1;white-space:nowrap;}
.hr-sidebar-sub{font-size:10px;color:${H.textFaint};letter-spacing:.06em;text-transform:uppercase;font-weight:700;white-space:nowrap;}
.hr-collapse-toggle{display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:9px;background:${H.goldSoft};border:none;color:${H.redDark};cursor:pointer;flex-shrink:0;transition:background .15s,color .15s;}
.hr-collapse-toggle:hover{background:${H.gold};color:#fff;}
.hr-sidebar-driver{padding:16px 20px;border-bottom:1px solid ${H.border};display:flex;align-items:center;gap:12px;}
.hr-sidebar-avatar{width:40px;height:40px;border-radius:50%;background:${H.goldSoft};border:1.5px solid ${H.gold};display:flex;align-items:center;justify-content:center;color:${H.goldDark};flex-shrink:0;overflow:hidden;}
.hr-sidebar-avatar img{width:100%;height:100%;object-fit:cover;border-radius:50%;}
.hr-sidebar-name{font-size:13.5px;font-weight:800;color:${H.dark};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.hr-sidebar-vehicle{font-size:10.5px;color:${H.textMuted};margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.hr-sidebar-status{padding:14px 20px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid ${H.border};}
.hr-online-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
.hr-online-dot.on{background:${H.green};animation:hrPulse 1.6s ease-in-out infinite;}
.hr-online-dot.off{background:${H.textFaint};}
.hr-sidebar-nav{flex:1;padding:12px 10px;}
.hr-nav-link{display:flex;align-items:center;gap:12px;padding:10px 12px;cursor:pointer;position:relative;transition:background .15s,color .15s;font-size:13.5px;font-weight:700;color:${H.dark};background:none;border:none;width:100%;text-align:left;font-family:var(--sans);border-radius:12px;margin-bottom:2px;}
.hr-nav-link.collapsed{justify-content:center;padding:10px;}
.hr-nav-link:hover{background:${H.goldSoft};}
.hr-nav-link.active{background:linear-gradient(135deg, ${H.red}, ${H.redDark});color:#fff;box-shadow:0 6px 16px rgba(200,32,46,.28);}
.hr-nav-icon{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;background:${H.goldSoft};color:${H.redDark};}
.hr-nav-link.active .hr-nav-icon{background:#fff;color:${H.red};}
.hr-nav-label{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.hr-nav-badge{margin-left:auto;background:${H.red};color:#fff;font-size:8px;font-weight:900;min-width:18px;height:18px;border-radius:9px;display:flex;align-items:center;justify-content:center;padding:0 5px;flex-shrink:0;}
.hr-sidebar-footer{padding:14px 20px;border-top:1px solid ${H.border};font-size:10px;color:${H.textFaint};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}

/* ── mobile header ── */
.hr-mob-brand{display:flex;align-items:center;gap:8px;flex:1;}
.hr-mob-brand img{width:26px;height:26px;border-radius:8px;object-fit:cover;border:1.5px solid ${H.gold};}
.hr-mob-brand span{font-family:var(--serif);font-size:16px;font-weight:700;color:${H.dark};}
.hr-mob-refresh{width:34px;height:34px;background:${H.goldSoft};border:none;border-radius:10px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:${H.redDark};transition:background .2s;}
.hr-mob-refresh:hover{background:${H.gold};color:#fff;}

/* ── main ── */
.hr-main-inner{padding:22px 18px 40px;max-width:900px;margin:0 auto;}
@media(min-width:960px){.hr-main-inner{padding:26px 26px 60px;}}

/* ── greeting hero ── */
.hr-greet{
  position:relative;overflow:hidden;
  background:linear-gradient(135deg, ${H.panel}, ${H.goldSoft});
  border:1px solid ${H.border};padding:22px;margin-bottom:20px;border-radius:20px;
  box-shadow:0 6px 20px rgba(27,14,6,0.06);
}
.hr-greet-body{display:flex;align-items:center;justify-content:space-between;gap:16px;}
.hr-greet-left{flex:1;}
.hr-greet-time{font-size:10px;font-weight:800;letter-spacing:2px;color:${H.goldDark};text-transform:uppercase;margin-bottom:6px;}
.hr-greet-name{font-family:var(--serif);font-size:clamp(24px,3.2vw,32px);font-weight:700;color:${H.dark};line-height:1.1;margin-bottom:8px;}
.hr-greet-status{display:flex;align-items:center;gap:7px;font-size:12.5px;font-weight:700;margin-bottom:8px;}
.hr-pending-alert{display:inline-flex;align-items:center;gap:7px;background:${H.redSoft};border:1px solid #F3B9BE;padding:7px 13px;cursor:pointer;color:${H.redDark};font-size:11.5px;font-weight:800;transition:background .2s;font-family:var(--sans);outline:none;border-radius:999px;}
.hr-pending-alert:hover{background:#F6CDD1;}
.hr-greet-art{position:relative;width:76px;height:76px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.hr-greet-icon{width:56px;height:56px;border-radius:50%;background:linear-gradient(135deg, ${H.red}, ${H.redDark});color:#fff;display:flex;align-items:center;justify-content:center;box-shadow:0 8px 20px rgba(200,32,46,.32);}

/* ── section header ── */
.hr-sec-hdr{display:flex;align-items:center;gap:8px;margin-bottom:14px;margin-top:26px;}
.hr-sec-eye{font-size:9px;font-weight:800;letter-spacing:2px;color:${H.goldDark};text-transform:uppercase;}
.hr-sec-line{flex:1;height:1px;background:linear-gradient(to right, ${H.border}, transparent);}
.hr-sec-title{font-family:var(--serif);font-size:17px;font-weight:700;color:${H.dark};}
.hr-see-all{display:flex;align-items:center;gap:3px;background:none;border:none;color:${H.goldDark};font-size:11.5px;font-weight:700;cursor:pointer;font-family:var(--sans);transition:color .2s;padding:0;}
.hr-see-all:hover{color:${H.redDark};}

/* ── ENHANCED FLEET CARD — matches DriverCars style ── */
.hr-fleet-card{border:1px solid ${H.border};background:${H.panel};margin-bottom:20px;overflow:hidden;border-radius:18px;box-shadow:0 4px 18px rgba(27,14,6,0.05);}

.hr-fleet-grid{display:grid;grid-template-columns:1fr;gap:16px;}

.hr-vehicle-card{background:${H.panel};border:2px solid transparent;border-radius:20px;box-shadow:0 4px 16px rgba(27,14,6,.06);transition:box-shadow .25s ease,transform .25s cubic-bezier(.22,1,.36,1),border-color .2s;overflow:hidden;display:flex;flex-direction:column;}
.hr-vehicle-card:hover{box-shadow:0 14px 30px rgba(27,14,6,.12);transform:translateY(-4px);}
.hr-vehicle-card.active{border-color:${H.green};box-shadow:0 0 0 3px ${H.greenSoft},0 10px 26px rgba(27,14,6,.10);}
.hr-vehicle-card.active:hover{box-shadow:0 0 0 3px ${H.greenSoft},0 16px 32px rgba(27,14,6,.14);}

.hr-vehicle-head{display:flex;align-items:center;gap:14px;padding:14px;cursor:pointer;}

.hr-vehicle-thumb{width:84px;height:84px;border-radius:16px;overflow:hidden;flex-shrink:0;position:relative;background:linear-gradient(135deg,${H.panelSoft},${H.bg});}
.hr-vehicle-thumb img{width:100%;height:100%;object-fit:cover;display:block;}

.hr-row-thumb-ph{width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:${H.goldDark};background:linear-gradient(135deg,${H.panelSoft},${H.bg});}
.hr-row-thumb-ph svg{width:30px;height:30px;opacity:.5;}

.hr-vehicle-active-dot{position:absolute;top:4px;right:4px;width:10px;height:10px;border-radius:50%;background:${H.green};border:2px solid #fff;z-index:2;}

.hr-vehicle-meta{flex:1;min-width:0;}
.hr-vehicle-title{font-family:var(--serif);font-size:17px;font-weight:700;color:${H.dark};margin-bottom:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.hr-vehicle-sub{font-size:12px;color:${H.textFaint};margin-bottom:8px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}

.hr-vehicle-status-pill{display:inline-flex;align-items:center;gap:7px;background:${H.bg};padding:5px 12px 5px 10px;border-radius:999px;font-size:12px;font-weight:600;color:${H.textMuted};}
.hr-vehicle-status-pill-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}

.hr-vehicle-side{display:flex;flex-direction:column;align-items:flex-end;gap:6px;flex-shrink:0;}
.hr-vehicle-rate{font-family:var(--serif);font-size:16px;font-weight:700;color:${H.redDark};}
.hr-vehicle-rate-unit{font-size:9px;color:${H.goldDark};font-weight:700;text-align:right;}
.hr-vehicle-chevron{color:${H.textFaint};display:flex;transition:transform .3s cubic-bezier(.22,1,.36,1);}
.hr-vehicle-chevron.open{transform:rotate(180deg);}

/* expand panel */
.hr-vehicle-expand-wrap{display:grid;grid-template-rows:0fr;transition:grid-template-rows .4s cubic-bezier(.22,1,.36,1);}
.hr-vehicle-expand-wrap.open{grid-template-rows:1fr;}
.hr-vehicle-expand-inner{overflow:hidden;min-height:0;}
.hr-vehicle-expand-page{transform-origin:top center;transform:scaleY(.97) rotateX(-6deg);opacity:0;transition:transform .38s cubic-bezier(.22,1,.36,1),opacity .3s ease;padding:0 16px 16px;border-top:1px solid ${H.border};margin-top:2px;}
.hr-vehicle-expand-wrap.open .hr-vehicle-expand-page{transform:scaleY(1) rotateX(0deg);opacity:1;}

/* spec grid */
.hr-spec-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:8px;margin:14px 0 4px;}
.hr-spec-item{display:flex;flex-direction:column;gap:2px;background:${H.bg};border:1px solid ${H.border};border-radius:12px;padding:8px 11px;}
.hr-spec-lbl{font-size:8.5px;font-weight:800;letter-spacing:1px;color:${H.textFaint};text-transform:uppercase;}
.hr-spec-val{font-size:13px;font-weight:700;color:${H.dark};font-family:var(--sans);}

.hr-vehicle-meta-row{display:flex;flex-wrap:wrap;gap:5px;margin:4px 0 10px;}
.hr-vehicle-pill{display:flex;align-items:center;gap:4px;padding:3px 9px;border:1px solid;font-size:9px;font-weight:700;border-radius:20px;}

.hr-vehicle-extras-row{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:10px;}
.hr-vehicle-extra-tag{background:${H.bg};border:1px solid ${H.border};padding:2px 8px;font-size:9px;font-weight:600;color:${H.textMuted};border-radius:8px;}

.hr-vehicle-loc-row{display:flex;align-items:center;gap:5px;margin-bottom:12px;font-size:10px;color:${H.textMuted};}

.hr-vehicle-desc-row{font-size:12px;color:${H.textMuted};line-height:1.6;margin-bottom:12px;}

.hr-vehicle-active-row{display:flex;align-items:center;justify-content:space-between;padding:9px 0 12px;border-top:1px dashed ${H.border};margin-top:2px;margin-bottom:10px;}
.hr-vehicle-active-btn{display:flex;align-items:center;gap:7px;cursor:pointer;background:none;border:none;padding:0;}
.hr-vehicle-active-circle{width:17px;height:17px;border-radius:50%;border:2px solid ${H.border};position:relative;flex-shrink:0;transition:border-color .15s;}
.hr-vehicle-active-circle.on{border-color:${H.green};}
.hr-vehicle-active-circle.on::after{content:"";position:absolute;inset:2.5px;border-radius:50%;background:${H.green};}
.hr-vehicle-active-label{font-size:10.5px;font-weight:700;color:${H.textFaint};}
.hr-vehicle-active-label.on{color:${H.green};}

.hr-vehicle-toggle-row{display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-bottom:1px solid ${H.border};}
.hr-vehicle-toggle-row:last-child{border-bottom:none;}
.hr-vehicle-toggle-lbl{font-size:12.5px;font-weight:700;color:${H.dark};}
.hr-vehicle-toggle-sub{font-size:10px;color:${H.textFaint};margin-top:1px;}

.hr-vehicle-tog{width:42px;height:24px;border-radius:12px;background:${H.border};position:relative;cursor:pointer;transition:background .2s;flex-shrink:0;}
.hr-vehicle-tog.on{background:${H.greenSoft};border:1px solid #BEE7CC;}
.hr-vehicle-tog-thumb{position:absolute;top:2px;left:2px;width:18px;height:18px;border-radius:50%;background:#fff;box-shadow:0 1px 4px rgba(27,14,6,.28);transition:left .2s,background .2s;}
.hr-vehicle-tog.on .hr-vehicle-tog-thumb{left:20px;background:${H.green};}

.hr-vehicle-actions{display:flex;gap:8px;}
.hr-vehicle-edit-btn{flex:1;display:flex;align-items:center;justify-content:center;gap:6px;background:${H.goldSoft};border:1px solid ${H.gold};padding:9px 0;cursor:pointer;font-size:10px;font-weight:800;letter-spacing:.6px;color:${H.goldDark};font-family:var(--sans);border-radius:12px;transition:background .2s,transform .15s;}
.hr-vehicle-edit-btn:hover{background:${H.gold};color:#fff;}
.hr-vehicle-edit-btn:active{transform:translateY(1px);}

.hr-vehicle-del-btn{width:38px;display:flex;align-items:center;justify-content:center;background:${H.redSoft};border:1px solid #F3B9BE;cursor:pointer;color:${H.redDark};border-radius:12px;transition:background .2s,transform .15s;}
.hr-vehicle-del-btn:hover{background:#F6CDD1;}
.hr-vehicle-del-btn:active{transform:translateY(1px);}

/* ── stats ── */
.hr-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:24px;}
@media(max-width:480px){.hr-stats{grid-template-columns:1fr 1fr;}}
.hr-stat{background:${H.panel};border:1px solid ${H.border};padding:18px 12px 14px;display:flex;flex-direction:column;align-items:center;gap:5px;transition:transform .2s,box-shadow .2s;border-radius:16px;}
.hr-stat.clickable{cursor:pointer;}
.hr-stat.clickable:hover{transform:translateY(-2px);box-shadow:0 8px 20px rgba(27,14,6,.08);}
.hr-stat-icon{width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;margin-bottom:2px;}
.hr-stat-val{font-family:var(--serif);font-size:23px;font-weight:700;color:${H.dark};line-height:1;}
.hr-stat-lbl{font-size:8px;font-weight:900;letter-spacing:.8px;color:${H.textMuted};text-transform:uppercase;}
.hr-stat-sub{font-size:9px;color:${H.textFaint};}

/* ── action tiles ── */
.hr-actions{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:28px;}
@media(min-width:640px){.hr-actions{grid-template-columns:repeat(4,1fr);}}
.hr-tile{background:${H.panel};border:1px solid ${H.border};padding:18px;display:flex;flex-direction:column;min-height:148px;cursor:pointer;position:relative;overflow:hidden;border-radius:18px;transition:transform .2s,box-shadow .2s,border-color .2s;}
.hr-tile:hover{transform:translateY(-3px);box-shadow:0 14px 30px rgba(27,14,6,.10);border-color:${H.gold};}
.hr-tile-icon{width:44px;height:44px;border-radius:50%;display:flex;align-items:center;justify-content:center;margin-bottom:12px;flex-shrink:0;}
.hr-tile-badge{position:absolute;top:10px;right:10px;background:${H.red};color:#fff;font-size:9px;font-weight:900;min-width:20px;height:20px;border-radius:10px;display:flex;align-items:center;justify-content:center;padding:0 5px;box-shadow:0 2px 8px rgba(200,32,46,.35);}
.hr-tile-label{font-size:12.5px;font-weight:800;color:${H.dark};margin-bottom:5px;}
.hr-tile-sub{font-size:10px;color:${H.textMuted};line-height:1.5;flex:1;}
.hr-tile-footer{display:flex;align-items:center;justify-content:space-between;margin-top:12px;}
.hr-tile-count{font-family:var(--serif);font-size:19px;font-weight:700;}
.hr-tile-arrow{width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;}

/* ── trip summary ── */
.hr-trips-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:0;margin-bottom:16px;border:1px solid ${H.border};border-radius:16px;overflow:hidden;}
@media(max-width:480px){.hr-trips-grid{grid-template-columns:1fr;}}
.hr-trip-rect{background:${H.panel};padding:18px 16px;cursor:pointer;transition:background .2s;position:relative;overflow:hidden;border-right:1px solid ${H.border};}
.hr-trip-rect:last-child{border-right:none;}
@media(max-width:480px){.hr-trip-rect{border-right:none;border-bottom:1px solid ${H.border};}.hr-trip-rect:last-child{border-bottom:none;}}
.hr-trip-rect:hover{background:${H.bg};}
.hr-trip-rect-icon{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;margin-bottom:10px;}
.hr-trip-rect-lbl{font-size:8px;font-weight:900;letter-spacing:1.5px;text-transform:uppercase;margin-bottom:6px;opacity:.75;}
.hr-trip-rect-val{font-family:var(--serif);font-size:30px;font-weight:700;color:${H.dark};line-height:1;margin-bottom:4px;}
.hr-trip-rect-sub{font-size:10px;color:${H.textFaint};}

.hr-trips-see-all{display:flex;align-items:center;justify-content:center;gap:8px;background:${H.panel};border:1px solid ${H.border};padding:13px;width:100%;cursor:pointer;color:${H.goldDark};font-size:11.5px;font-weight:800;font-family:var(--sans);transition:border-color .2s,color .2s,background .2s;margin-bottom:28px;border-radius:999px;}
.hr-trips-see-all:hover{border-color:${H.gold};color:${H.redDark};background:${H.goldSoft};}
.hr-trips-loader{padding:32px;display:flex;align-items:center;justify-content:center;gap:10px;color:${H.textMuted};font-size:12px;}

/* ── nudge ── */
.hr-nudge{display:flex;align-items:center;gap:12px;background:${H.amberSoft};border:1px solid ${H.gold};padding:16px;cursor:pointer;margin-bottom:12px;transition:background .2s;border-radius:16px;}
.hr-nudge:hover{background:#F9E3AE;}
.hr-nudge-icon{width:36px;height:36px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;color:${H.amber};flex-shrink:0;}
.hr-nudge-title{font-size:13px;font-weight:800;color:${H.dark};margin-bottom:2px;}
.hr-nudge-sub{font-size:11px;color:${H.textMuted};line-height:1.5;}

/* ── tab bar ── */
.hr-tabbar{position:fixed;bottom:0;left:0;right:0;background:${H.panel};border-top:1px solid ${H.border};display:flex;padding:8px 0 max(6px,env(safe-area-inset-bottom));z-index:50;box-shadow:0 -4px 16px rgba(27,14,6,0.06);}
.hr-tab{flex:1;display:flex;flex-direction:column;align-items:center;gap:3px;cursor:pointer;background:none;border:none;font-family:var(--sans);padding:4px 0;position:relative;transition:color .15s;color:${H.textFaint};}
.hr-tab.on{color:${H.red};}
.hr-tab-label{font-size:9.5px;font-weight:700;}
.hr-tab-badge{position:absolute;top:-2px;right:calc(50% - 18px);background:${H.red};color:#fff;font-size:7px;font-weight:900;min-width:14px;height:14px;border-radius:7px;display:flex;align-items:center;justify-content:center;padding:0 4px;}

.hr-spin{animation:hrSpin .8s linear infinite;}
@keyframes hrSpin{to{transform:rotate(360deg);}}
@keyframes hrPulse{0%,100%{opacity:.5;transform:scale(1);}50%{opacity:1;transform:scale(1.35);}}
`;

/* ══════════════════════════════════
   ENHANCED VEHICLE CARD COMPONENT
══════════════════════════════════ */
function VehicleCard({
  listing,
  pingRef,
  onEdit,
  onDelete,
  onSetActive,
}: {
  listing: any;
  pingRef: React.MutableRefObject<Record<string, ReturnType<typeof setInterval>>>;
  onEdit: (listing: any) => void;
  onDelete: (listing: any) => void;
  onSetActive: (listing: any) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean>(listing.isOnline ?? false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    const dId = listing.listingId;
    if (!dId) return;
    const ref = dbRef(rtdb, `onlineDrivers/${dId}`);
    const unsubscribe = onValue(ref, (snap) => {
      const v = snap.val();
      setIsOnline(v ? true : false);
    });
    return () => off(ref);
  }, [listing.listingId]);

  const startPing = (id: string) => {
    if (pingRef.current[id]) clearInterval(pingRef.current[id]);
    const send = () => {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) =>
          fetch(DRIVER_PING, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ driverId: id, lat: coords.latitude, lng: coords.longitude, isAvailable: true }),
          }).catch(() => {}),
        () =>
          fetch(DRIVER_PING, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ driverId: id, lat: 0, lng: 0, isAvailable: true }),
          }).catch(() => {})
      );
    };
    send();
    pingRef.current[id] = setInterval(send, 8000);
  };

  const stopPing = (id: string) => {
    if (pingRef.current[id]) {
      clearInterval(pingRef.current[id]);
      delete pingRef.current[id];
    }
  };

  const toggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const dId = listing.driverId || listing.listingId;
    if (!dId || toggling) return;
    const next = !isOnline;
    setToggling(true);
    setIsOnline(next);
    try {
      await fetch(next ? DRIVER_ONLINE : DRIVER_OFFLINE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ driverId: dId }),
      });
      if (next) {
        await set(dbRef(rtdb, `onlineDrivers/${dId}`), { isAvailable: true, lat: 0, lng: 0, ts: Date.now() });
        startPing(dId);
      } else {
        await remove(dbRef(rtdb, `onlineDrivers/${dId}`));
        stopPing(dId);
      }
    } catch {
      setIsOnline(!next);
    } finally {
      setToggling(false);
    }
  };

  const brandLabel = listing.brand ? carBrandName(listing.brand) : "";
  const extArr = extrasToArr(listing.extras);
  const discKey = discKeyFromObj(listing.discountApplied);
  const statusColor = isOnline ? "#15803D" : H.textFaint;
  const statusLabel = isOnline ? "Online" : "Offline";

  return (
    <div className={`hr-vehicle-card${listing.isSelected ? " active" : ""}`}>
      <div className="hr-vehicle-head" onClick={() => setExpanded((p) => !p)}>
        <div className="hr-vehicle-thumb">
          <RowThumb listing={listing} />
          {listing.isSelected && <div className="hr-vehicle-active-dot" title="Active vehicle" />}
        </div>

        <div className="hr-vehicle-meta">
          <div className="hr-vehicle-title">
            {brandLabel} {listing.model || vehicleName(listing.vehicleType)}
          </div>
          <div className="hr-vehicle-sub">
            {listing.licensePlate || "No plate set"}
            {listing.city ? ` · ${listing.city}` : ""}
          </div>
          <div className="hr-vehicle-status-pill">
            <span className="hr-vehicle-status-pill-dot" style={{ background: statusColor }} />
            {statusLabel}
          </div>
        </div>

        <div className="hr-vehicle-side">
          <div className="hr-vehicle-rate">
            {Number.isFinite(listing.rate) ? `€${listing.rate}` : "—"}
          </div>
          <div className="hr-vehicle-rate-unit">{rateUnit(listing.rateType)}</div>
          <div className={`hr-vehicle-chevron${expanded ? " open" : ""}`}>
            <ChevronDown size={16} />
          </div>
        </div>
      </div>

      <div className={`hr-vehicle-expand-wrap${expanded ? " open" : ""}`}>
        <div className="hr-vehicle-expand-inner">
          <div className="hr-vehicle-expand-page">
            <div className="hr-spec-grid">
              <SpecItem label="Type" value={vehicleName(listing.vehicleType)} />
              <SpecItem label="Fuel" value={fuelTypeName(listing.fuelType)} />
              <SpecItem label="Transmission" value={transmissionName(listing.transmission)} />
              <SpecItem label="Class" value={classTypeName(listing.classType)} />
              <SpecItem label="Size" value={carSizeName(listing.carSize)} />
              <SpecItem label="Seats" value={listing.capacity ? String(listing.capacity) : "—"} />
              <SpecItem label="Year" value={listing.year ? String(listing.year) : "—"} />
            </div>

            <div className="hr-vehicle-meta-row">
              {(listing.photos?.length || 0) > 1 && (
                <div className="hr-vehicle-pill" style={{ borderColor: H.border, background: H.bg, color: H.textMuted }}>
                  <ImageIcon size={9} /> {listing.photos.length} photos
                </div>
              )}
              {listing.rideSharing && (
                <div className="hr-vehicle-pill" style={{ borderColor: H.blue + "44", background: H.blueSoft, color: H.blue }}>
                  <GitMerge size={9} /> Sharing
                </div>
              )}
              {listing.allowParcel && (
                <div className="hr-vehicle-pill" style={{ borderColor: H.amber + "44", background: H.amberSoft, color: H.amber }}>
                  <Package size={9} /> Parcels
                </div>
              )}
              {discKey !== "None" && (
                <div className="hr-vehicle-pill" style={{ borderColor: H.gold + "55", background: H.goldSoft, color: H.goldDark }}>
                  <Tag size={9} /> {discKey} off
                </div>
              )}
            </div>

            {listing.description && <div className="hr-vehicle-desc-row">{listing.description}</div>}

            {extArr.length > 0 && (
              <div className="hr-vehicle-extras-row">
                {extArr.map((e) => (
                  <span key={e.name} className="hr-vehicle-extra-tag">
                    +{e.name} €{e.price}
                  </span>
                ))}
              </div>
            )}

            {listing.city && (
              <div className="hr-vehicle-loc-row">
                <MapPin size={11} />
                {listing.city}
                {listing.country ? `, ${listing.country}` : ""}
                {listing.serviceRadiusKm ? ` · ${listing.serviceRadiusKm}km radius` : ""}
              </div>
            )}

            <div className="hr-vehicle-active-row">
              <button
                className="hr-vehicle-active-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onSetActive(listing);
                }}
              >
                <div className={`hr-vehicle-active-circle${listing.isSelected ? " on" : ""}`} />
                <span className={`hr-vehicle-active-label${listing.isSelected ? " on" : ""}`}>
                  {listing.isSelected ? "Active Vehicle" : "Set as Active"}
                </span>
              </button>
            </div>

            <div className="hr-vehicle-toggle-row">
              <div>
                <div className="hr-vehicle-toggle-lbl" style={{ color: toggling ? H.textFaint : isOnline ? "#15803D" : H.dark }}>
                  {toggling ? "Updating…" : isOnline ? "Online — accepting rides" : "Go online"}
                </div>
                <div className="hr-vehicle-toggle-sub">
                  {isOnline ? "Pinging location every 8s" : "Toggle to accept rides with this vehicle"}
                </div>
              </div>
              <div
                className={`hr-vehicle-tog${isOnline ? " on" : ""}`}
                style={toggling ? { opacity: 0.5, pointerEvents: "none" } : undefined}
                onClick={toggle}
              >
                <div className="hr-vehicle-tog-thumb" />
              </div>
            </div>

            <div className="hr-vehicle-actions">
              <button
                className="hr-vehicle-edit-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(listing);
                }}
              >
                <Edit3 size={13} /> EDIT
              </button>
              <button
                className="hr-vehicle-del-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(listing);
                }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   MAIN DASHBOARD
══════════════════════════════════ */
export default function DriverDashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((s: RootState) => s.auth);
  const driver = useSelector(selectCurrentDriver);
  const pendingTrip = useSelector(selectPendingTripRequest);
  const pendingParcel = useSelector(selectPendingParcelRequest);

  const userId = (user as any)?.userId || (user as any)?.uid;
  const firstName = (user as any)?.firstName || (user as any)?.fullName?.split(" ")?.[0] || "Driver";

  const listings: any[] = useSelector(selectListings);

  const [tripStats, setTripStats] = useState({ total: 0, completed: 0, earnings: 0, rating: 0, ratingCount: 0 });
  const [loadingTrips, setLoadingTrips] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("Home");
  const [mounted, setMounted] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [delTarget, setDelTarget] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  const pingRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const anyOnline = listings.some((l) => l.isOnline);

  useEffect(() => {
    const id = "hr-css";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.textContent = CSS;
      document.head.appendChild(tag);
    }
    requestAnimationFrame(() => setMounted(true));
  }, []);

  useEffect(() => {
    if (!userId) return;
    const a = dispatch(listenToDriverData(userId) as any);
    const b = dispatch(listenToDriverTripRequests(userId) as any);
    const c = dispatch(listenToDriverParcelRequests(userId) as any);
    return () => {
      if (typeof a === "function") a();
      if (typeof b === "function") b();
      if (typeof c === "function") c();
      Object.values(pingRef.current).forEach(clearInterval);
    };
  }, [userId, dispatch]);

  const fetchData = useCallback(async () => {
    if (!userId) return;
    try {
      const snap = await getDocs(
        query(collection(db, "rideTrips"), where("driverId", "==", userId), limit(50))
      );
      const trips = snap.docs
        .map((d) => ({ id: d.id, ...d.data() } as any))
        .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
      const completed = trips.filter((t: any) => t.status === "completed");
      const earnings = completed.reduce((s: number, t: any) => s + (t.driverProfit || 0), 0);
      setTripStats({
        total: trips.length,
        completed: completed.length,
        earnings: +earnings.toFixed(2),
        rating: (driver as any)?.rating ?? 0,
        ratingCount: (driver as any)?.ratingCount ?? 0,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingTrips(false);
    }
  }, [userId, driver]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleDelete = useCallback(async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await updateDoc(doc(db, "Fleet", delTarget.listingId), { isDeleted: true, updatedAt: serverTimestamp() });
      setDelTarget(null);
    } catch (e) {
      alert((e as any).message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }, [delTarget]);

  const handleSetActive = useCallback(
    (listing: any) => {
      // hostId owns the Fleet brand; userId is THIS driver. Dispatch handles
      // the toggle (select vs deselect) and only ever clears this driver's
      // own prior pick — other drivers' active vehicles are left alone.
      if (!userId || !listing?.listingId || !listing?.hostId) return;
      dispatch(setActiveVehicle({ hostId: listing.hostId, listingId: listing.listingId, userId }));
    },
    [userId, dispatch]
  );

  const totalRequests = (pendingTrip ? 1 : 0) + (pendingParcel ? 1 : 0);
  const parcelRequestCount = pendingParcel ? 1 : 0;

  const NAV_ITEMS = [
    { key: "Home", label: "Home", Icon: Home, color: H.blue },
    { key: "Requests", label: "Requests", Icon: Bell, color: H.red, badge: totalRequests },
    { key: "Cars", label: "My Fleet", Icon: Layers, color: H.goldDark },
    { key: "Parcels", label: "Parcels", Icon: Package, color: H.amber, badge: parcelRequestCount },
    { key: "History", label: "History", Icon: Navigation, color: H.blue },
    { key: "Profile", label: "Profile", Icon: User, color: H.blue },
  ];

  const ACTION_TILES = [
    { key: "Requests", label: "Ride Requests", sub: "Accept or decline incoming requests", color: H.red, Icon: Bell, badge: totalRequests, count: totalRequests, countLabel: totalRequests === 1 ? "request" : "requests" },
    { key: "Cars", label: "My Fleet", sub: "Manage vehicles, rates and availability", color: H.goldDark, Icon: Layers, badge: null, count: listings.length, countLabel: listings.length === 1 ? "vehicle" : "vehicles" },
    { key: "Parcels", label: "Parcels", sub: "Active deliveries and parcel history", color: H.amber, Icon: Package, badge: parcelRequestCount, count: parcelRequestCount, countLabel: "pending" },
    { key: "Profile", label: "Profile", sub: "Account settings and documents", color: H.blue, Icon: User, badge: null, count: null, countLabel: "" },
  ];

  const TRIP_RECTS = [
    { label: "Total", val: String(tripStats.total), sub: "all time trips", color: H.blue, icon: <Navigation size={13} /> },
    { label: "Completed", val: String(tripStats.completed), sub: "successful rides", color: "#15803D", icon: <Star size={13} /> },
    { label: "Earnings", val: `€${tripStats.earnings}`, sub: "driver payout", color: H.goldDark, icon: <TrendingUp size={13} /> },
  ];

  const renderInlinePage = () => {
    switch (activeTab) {
      case "Requests":
        return (
          <div className="hr-panel">
            <RideBookings />
          </div>
        );
      case "Cars":
        return (
          <div className="hr-panel">
            <DriverCars />
          </div>
        );
      case "Parcels":
        return (
          <div className="hr-panel">
            <ParcelBookings />
          </div>
        );
      default:
        return null;
    }
  };

  const isHomePage = activeTab === "Home";

  const DashContent = (
    <div className="hr-main-inner">
      {/* ── GREETING ── */}
      <div className="hr-greet hr-reveal" style={{ "--d": "60ms" } as any}>
        <div className="hr-greet-body">
          <div className="hr-greet-left">
            <div className="hr-greet-time">{getGreeting()}</div>
            <div className="hr-greet-name">
              {firstName} <span aria-hidden="true"></span>
            </div>
            <div className="hr-greet-status">
              <div className={`hr-online-dot${anyOnline ? " on" : " off"}`} />
              <span style={{ color: anyOnline ? "#15803D" : H.textMuted }}>
                {anyOnline ? "You're live — accepting rides" : "You're offline"}
              </span>
            </div>
            {totalRequests > 0 && (
              <button className="hr-pending-alert" onClick={() => setActiveTab("Requests")}>
                <Bell size={12} />
                {totalRequests} new {totalRequests === 1 ? "request" : "requests"} waiting
                <ChevronRight size={12} />
              </button>
            )}
          </div>
          <div className="hr-greet-art">
            <div className="hr-greet-icon">
              <Heart size={22} fill="#fff" />
            </div>
          </div>
        </div>
      </div>

      {/* ── FLEET CARD ── */}
      <div className="hr-sec-hdr hr-reveal" style={{ "--d": "90ms" } as any}>
        <span className="hr-sec-eye">Fleet</span>
        <div className="hr-sec-line" />
        <span className="hr-sec-title">Your Vehicles</span>
        <button className="hr-see-all" onClick={() => setActiveTab("Cars")} style={{ marginLeft: 12 }}>
          Manage <ChevronRight size={11} />
        </button>
      </div>

      <div className="hr-reveal" style={{ "--d": "110ms" } as any}>
        {listings.length === 0 ? (
          <div className="hr-nudge" onClick={() => setActiveTab("Cars")}>
            <div className="hr-nudge-icon">
              <AlertCircle size={18} />
            </div>
            <div>
              <div className="hr-nudge-title">No vehicles registered</div>
              <div className="hr-nudge-sub">Add your vehicle to start accepting rides.</div>
            </div>
            <ChevronRight size={16} color={H.amber} style={{ marginLeft: "auto", flexShrink: 0 }} />
          </div>
        ) : (
          <div className="hr-fleet-card">
            <div className="hr-fleet-grid">
              {listings.slice(0, 2).map((listing: any, i: number) => (
                <VehicleCard
                  key={listing.listingId || listing.driverId || i}
                  listing={listing}
                  pingRef={pingRef}
                  onEdit={() => setActiveTab("Cars")}
                  onDelete={setDelTarget}
                  onSetActive={handleSetActive}
                />
              ))}
              {listings.length > 2 && (
                <button className="hr-tile" onClick={() => setActiveTab("Cars")} style={{ border: `2px dashed ${H.border}`, background: "transparent", cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: H.goldDark }}>
                      +{listings.length - 2} More
                    </span>
                    <ChevronRight size={14} color={H.goldDark} />
                  </div>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── STATS ── */}
      <div className="hr-sec-hdr hr-reveal" style={{ "--d": "160ms" } as any}>
        <span className="hr-sec-eye">Overview</span>
        <div className="hr-sec-line" />
        <span className="hr-sec-title">Your Stats</span>
        <button className="hr-see-all" onClick={handleRefresh} style={{ marginLeft: 12 }}>
          <RefreshCw size={11} className={refreshing ? "hr-spin" : ""} />
        </button>
      </div>
      <div className="hr-stats hr-reveal" style={{ "--d": "190ms" } as any}>
        {[
          { label: "Rating", val: tripStats.rating > 0 ? tripStats.rating.toFixed(1) : "—", sub: tripStats.ratingCount > 0 ? `${tripStats.ratingCount} reviews` : "no reviews", color: H.goldDark, icon: <Star size={15} /> },
          { label: "Earnings", val: `€${tripStats.earnings}`, sub: "total payout", color: "#15803D", icon: <TrendingUp size={15} />, tab: "History" },
          { label: "Trips", val: String(tripStats.completed), sub: "completed", color: H.blue, icon: <Navigation size={15} />, tab: "History" },
        ].map(({ label, val, sub, color, icon, tab }: any) => (
          <div key={label} className={`hr-stat${tab ? " clickable" : ""}`} onClick={() => tab && setActiveTab(tab)}>
            <div className="hr-stat-icon" style={{ background: color + "18" }}>
              <span style={{ color }}>{icon}</span>
            </div>
            <div className="hr-stat-val">{val}</div>
            <div className="hr-stat-lbl">{label}</div>
            <div className="hr-stat-sub">{sub}</div>
          </div>
        ))}
      </div>

      {/* ── ACTION TILES ── */}
      <div className="hr-sec-hdr hr-reveal" style={{ "--d": "230ms" } as any}>
        <span className="hr-sec-eye">Quick Access</span>
        <div className="hr-sec-line" />
        <span className="hr-sec-title">Actions</span>
      </div>
      <div className="hr-actions hr-reveal" style={{ "--d": "260ms" } as any}>
        {ACTION_TILES.map(({ key, label, sub, color, Icon: Ic, badge, count, countLabel }) => (
          <div key={key} className="hr-tile" onClick={() => (key === "Profile" ? navigate(`/profile/${userId}`) : setActiveTab(key))}>
            <div className="hr-tile-icon" style={{ background: color + "18" }}>
              <Ic size={20} color={color} />
            </div>
            {badge != null && badge > 0 && <div className="hr-tile-badge">{badge > 99 ? "99+" : badge}</div>}
            <div className="hr-tile-label">{label}</div>
            <div className="hr-tile-sub">{sub}</div>
            <div className="hr-tile-footer">
              {count != null && (
                <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                  <span className="hr-tile-count" style={{ color }}>{count}</span>
                  <span style={{ fontSize: 9, color: H.textFaint, fontWeight: 700 }}>{countLabel}</span>
                </div>
              )}
              <div className="hr-tile-arrow" style={{ background: color + "18", marginLeft: "auto" }}>
                <ArrowRight size={12} color={color} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── TRIP SUMMARY ── */}
      <div className="hr-sec-hdr hr-reveal" style={{ "--d": "300ms" } as any}>
        <span className="hr-sec-eye">History</span>
        <div className="hr-sec-line" />
        <span className="hr-sec-title">Trip Summary</span>
        <button className="hr-see-all" onClick={() => setActiveTab("History")} style={{ marginLeft: 12 }}>
          See all <ChevronRight size={11} />
        </button>
      </div>

      {loadingTrips ? (
        <div className="hr-trips-loader hr-reveal" style={{ "--d": "320ms" } as any}>
          <Loader2 size={18} className="hr-spin" color={H.goldDark} /> Loading…
        </div>
      ) : (
        <div className="hr-trips-grid hr-reveal" style={{ "--d": "320ms" } as any}>
          {TRIP_RECTS.map(({ label, val, sub, color, icon }) => (
            <div key={label} className="hr-trip-rect" onClick={() => setActiveTab("History")}>
              <div className="hr-trip-rect-icon" style={{ background: color + "18" }}>
                <span style={{ color }}>{icon}</span>
              </div>
              <div className="hr-trip-rect-lbl" style={{ color }}>{label}</div>
              <div className="hr-trip-rect-val">{val}</div>
              <div className="hr-trip-rect-sub">{sub}</div>
            </div>
          ))}
        </div>
      )}

      <button className="hr-trips-see-all" onClick={() => setActiveTab("History")}>
        View Full Trip History <ChevronRight size={13} />
      </button>
    </div>
  );

  return (
    <div className={`hr-root${mounted ? " hr-on" : ""}`}>
      {/* Mobile header */}
      <header className="hr-mobile-header">
        <div className="hr-mob-brand">
          <img
            src={appLogo}
            alt="HeartRide"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
          <span>HeartRide</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div className={`hr-online-dot${anyOnline ? " on" : " off"}`} />
          <span style={{ fontSize: 10, fontWeight: 700, color: anyOnline ? "#15803D" : H.textMuted }}>
            {anyOnline ? "Online" : "Offline"}
          </span>
        </div>
        <button className="hr-mob-refresh" onClick={handleRefresh}>
          <RefreshCw size={14} className={refreshing ? "hr-spin" : ""} />
        </button>
      </header>

      <div className="hr-body">
        {/* Desktop sidebar */}
        <aside className="hr-sidebar" style={{ width: collapsed ? 76 : 250 }}>
          <div className="hr-sidebar-logo">
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <img
                src={appLogo}
                alt="HeartRide"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
              {!collapsed && (
                <div style={{ minWidth: 0 }}>
                  <div className="hr-sidebar-brand">HeartRide</div>
                  <div className="hr-sidebar-sub">Driver Dashboard</div>
                </div>
              )}
            </div>
            <button
              className="hr-collapse-toggle"
              onClick={() => setCollapsed((p) => !p)}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
            </button>
          </div>

          <div className="hr-sidebar-driver">
            <div className="hr-sidebar-avatar">
              {(user as any)?.profilePicture ? <img src={(user as any).profilePicture} alt="" /> : <User size={18} />}
            </div>
            {!collapsed && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="hr-sidebar-name">{firstName}</div>
                <div className="hr-sidebar-vehicle">
                  {listings.length} {listings.length === 1 ? "vehicle" : "vehicles"} · {listings.filter((l) => l.isOnline).length} online
                </div>
              </div>
            )}
          </div>

          <div className="hr-sidebar-status" style={{ justifyContent: collapsed ? "center" : "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div className={`hr-online-dot${anyOnline ? " on" : " off"}`} />
              {!collapsed && (
                <span style={{ fontSize: 12, fontWeight: 700, color: anyOnline ? "#15803D" : H.textMuted }}>
                  {anyOnline ? "Online" : "Offline"}
                </span>
              )}
            </div>
            {!collapsed && (
              <span style={{ fontSize: 10, color: H.textFaint }}>
                {listings.filter((l) => l.isOnline).length}/{listings.length} live
              </span>
            )}
          </div>

          <nav className="hr-sidebar-nav">
            {NAV_ITEMS.map(({ key, label, Icon: Ic, color, badge }) => (
              <button
                key={key}
                className={`hr-nav-link${activeTab === key ? " active" : ""}${collapsed ? " collapsed" : ""}`}
                onClick={() => (key === "Profile" ? navigate(`/profile/${userId}`) : setActiveTab(key))}
                title={label}
              >
                <div className="hr-nav-icon">
                  <Ic size={15} color={activeTab === key ? H.red : color} />
                </div>
                {!collapsed && <span className="hr-nav-label">{label}</span>}
                {!collapsed && badge != null && badge > 0 && <span className="hr-nav-badge">{badge > 9 ? "9+" : badge}</span>}
              </button>
            ))}
          </nav>

          {!collapsed && <div className="hr-sidebar-footer">© {new Date().getFullYear()} HeartRide, by Heartlas</div>}
        </aside>

        {/* Main content */}
        <main className="hr-main">{isHomePage ? DashContent : renderInlinePage()}</main>
      </div>

      {/* Mobile tab bar */}
      <nav className="hr-tabbar">
        {[
          { key: "Home", label: "Home", Icon: Home, badge: undefined },
          { key: "Requests", label: "Requests", Icon: Bell, badge: totalRequests },
          { key: "Cars", label: "Fleet", Icon: Layers, badge: undefined },
          { key: "Profile", label: "Profile", Icon: User, badge: undefined },
        ].map(({ key, label, Icon: Ic, badge }) => {
          const on = activeTab === key;
          return (
            <button
              key={key}
              className={`hr-tab${on ? " on" : ""}`}
              onClick={() => (key === "Profile" ? navigate(`/profile/${userId}`) : setActiveTab(key))}
            >
              <Ic size={22} color={on ? H.red : H.textFaint} />
              <span className="hr-tab-label">{label}</span>
              {badge != null && badge > 0 && <div className="hr-tab-badge">{badge > 9 ? "9+" : badge}</div>}
            </button>
          );
        })}
      </nav>

      {/* DELETE CONFIRM MODAL */}
      {delTarget && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(27,14,6,.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 400,
            padding: 20,
          }}
          onClick={() => !deleting && setDelTarget(null)}
        >
          <div
            style={{
              background: H.panel,
              padding: 24,
              borderRadius: 20,
              maxWidth: 360,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              border: `1px solid ${H.border}`,
              boxShadow: "0 30px 70px rgba(27,14,6,.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: H.red }}>
              <AlertCircle size={20} />
              <div style={{ fontSize: 16, fontWeight: 800, color: H.dark }}>Delete Listing</div>
            </div>
            <div style={{ fontSize: 13, color: H.textMuted, lineHeight: 1.6 }}>
              Are you sure you want to delete <strong style={{ color: H.dark }}>"{delTarget.model || "this listing"}"</strong>? This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <button
                style={{
                  flex: 1,
                  padding: "12px 0",
                  background: H.panelSoft,
                  border: `1px solid ${H.border}`,
                  cursor: "pointer",
                  color: H.textMuted,
                  fontSize: 10.5,
                  fontWeight: 800,
                  borderRadius: 999,
                  fontFamily: "var(--sans)",
                }}
                onClick={() => setDelTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                style={{
                  flex: 1,
                  padding: "12px 0",
                  background: H.red,
                  border: "none",
                  cursor: "pointer",
                  color: "#fff",
                  fontSize: 10.5,
                  fontWeight: 800,
                  borderRadius: 999,
                  fontFamily: "var(--sans)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  boxShadow: "0 4px 12px rgba(200,32,46,.28)",
                }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? <Loader2 size={13} className="hr-spin" /> : <Trash2 size={13} />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}